import { Check, Close, ContentCopy, MoreHoriz, TransferWithinAStation } from '@mui/icons-material';
import { Grid } from '@mui/material';
import { Language } from '@nx-smartmonkey/shared/domain';
import { dateFormat, pastDateTime } from '@nx-smartmonkey/shared/helpers';
import { HSpacer, RoutalPalette, RoutalTooltip } from '@nx-smartmonkey/ui';

import IntercomIcon from '../../../../../assets/img/backoffice/intercom.svg';
import PipedriveIcon from '../../../../../assets/img/backoffice/pipedrive.svg';
import StripeIcon from '../../../../../assets/img/backoffice/stripe.svg';
import { UsersTableColumn } from '../../../../../context/backoffice/tableColumns/users';
import { User as BackofficeUser } from '../../../../../domain/backoffice/user/User';
import { UserBaseValidation } from '../../../../../domain/backoffice/user/UserBase';
import { User } from '../../../../../domain/user/User';
import Button from '../../../../../react-components/Button';
import Text from '../../../../../react-components/Text';
import { AlignmentTypes, DataTableColumnProps } from '../../../../../react-components/data-table/DataTable';
import {
  generateIntercomLink,
  generatePipedriveLink,
  generateStripeCustomerLink,
} from '../../../shared/SystemsInfoBox';
import { BackofficeClientsUserActionsMenu } from '../shared/BackofficeClientsUserActionsMenu';

type UsersGetColumnsResponse = { [key: string]: any }[];

type UsersGetColumns = {
  loggedUser?: User;
  adminUser?: User;
  tableColumns: Array<UsersTableColumn>;
  handleAsUser: (userId: string) => void;
  handleCopyEmail: (email: string) => void;
  onRefreshContent: () => void;
  goToDetails: ({ target, id }: { target: `user` | `organization` | `project`; id: string }) => void;
};

export const getUsersColumns = ({
  loggedUser,
  adminUser,
  tableColumns,
  handleAsUser,
  handleCopyEmail,
  onRefreshContent,
  goToDetails,
}: UsersGetColumns): UsersGetColumnsResponse => {
  const columns: Array<DataTableColumnProps> = [];

  tableColumns.forEach(({ displayable, identifier, sortable }: UsersTableColumn) => {
    if (displayable) {
      switch (identifier) {
        case `email`:
          columns.push({
            width: 240,
            columnStyle: {
              width: `unset`,
            },
            accessorName: `email`,
            align: `justify` as AlignmentTypes,
            sortable,
            name: <Text style={{ color: RoutalPalette.primary20 }} isBold intlId="backoffice.clients.users.email" />,
            formatter: ({ doc: user, rowHover }: { doc: BackofficeUser; rowHover: boolean }) => {
              if (!user.email) {
                return undefined;
              }

              return (
                <Grid container direction="row" alignItems="center" justifyContent="center">
                  <RoutalTooltip title={user.email}>
                    <Text
                      variant="button"
                      style={{ maxWidth: `220px` }}
                      onClick={(e: any) => goToDetails({ target: `user`, id: user.id })}
                    >
                      {user.email}
                    </Text>
                  </RoutalTooltip>

                  <HSpacer small />

                  {rowHover ? (
                    <ContentCopy
                      style={{
                        fontSize: `16px`,
                        color: RoutalPalette.secondary00,
                        cursor: `pointer`,
                      }}
                      onClick={(e: any) => {
                        handleCopyEmail(user.email);
                      }}
                    />
                  ) : null}
                </Grid>
              );
            },
          });
          break;
        case `external_services`:
          columns.push({
            width: 120,
            columnStyle: {
              width: `unset`,
            },
            align: `center` as AlignmentTypes,
            sortable,
            formatter: ({ doc: user }: { doc: BackofficeUser }) => (
              <Grid container direction="row">
                <img
                  src={IntercomIcon}
                  style={{ width: `18px`, cursor: user.email ? `pointer` : `default`, opacity: user.email ? 1 : 0.5 }}
                  alt=""
                  draggable="false"
                  onClick={() => {
                    if (user.email) {
                      window.open(generateIntercomLink(user.email), `_blank`);
                    }
                  }}
                />
                <HSpacer small />
                <img
                  src={StripeIcon}
                  style={{
                    width: `18px`,
                    cursor: user.organization.subscription.stripe_customer_id ? `pointer` : `default`,
                    opacity: user.organization.subscription.stripe_customer_id ? 1 : 0.5,
                  }}
                  alt=""
                  draggable="false"
                  onClick={() => {
                    if (user.organization.subscription.stripe_customer_id) {
                      window.open(
                        generateStripeCustomerLink(user.organization.subscription.stripe_customer_id),
                        `_blank`
                      );
                    }
                  }}
                />
                <HSpacer small />
                <img
                  src={PipedriveIcon}
                  style={{
                    width: `18px`,
                    cursor: user.organization.subscription.pipedrive_person_id ? `pointer` : `default`,
                    opacity: user.organization.subscription.pipedrive_person_id ? 1 : 0.5,
                  }}
                  alt=""
                  draggable="false"
                  onClick={() => {
                    if (user.organization.subscription.pipedrive_person_id) {
                      window.open(generatePipedriveLink(user.organization.subscription.pipedrive_person_id), `_blank`);
                    }
                  }}
                />
              </Grid>
            ),
          });
          break;
        case `name`:
          columns.push({
            width: 200,
            height: 45,
            sortable,
            accessorName: `name`,
            name: <Text style={{ color: RoutalPalette.primary20 }} isBold intlId="backoffice.clients.users.name" />,
            formatter: ({ doc: user }: { doc: BackofficeUser }) => {
              const name = `${user.first_name ?? ``} ${user.last_name ?? ``}`;
              const deleted = user.deleted;

              return (
                <Grid container direction="row" alignItems="center">
                  {name && name !== `` ? (
                    <RoutalTooltip title={name}>
                      <Text style={{ fontWeight: 500, ...(deleted ? { textDecoration: `line-through` } : {}) }}>
                        {name.length > 35 ? `${name.substring(0, 35)}...` : `${name}`}
                      </Text>
                    </RoutalTooltip>
                  ) : (
                    <Text
                      style={{
                        fontWeight: 500,
                        ...(deleted ? { textDecoration: `line-through` } : {}),
                        color: RoutalPalette.secondary00,
                      }}
                      intlId="no_name"
                    />
                  )}
                </Grid>
              );
            },
          });
          break;
        case `company`:
          columns.push({
            width: 200,
            height: 45,
            sortable,
            accessorName: `company`,
            name: <Text style={{ color: RoutalPalette.primary20 }} isBold intlId="backoffice.clients.users.company" />,
            formatter: ({ doc: user }: { doc: BackofficeUser }) => {
              const { organization } = user;
              const name = `${organization.label ?? ``}`;

              return (
                <Grid container direction="row" alignItems="center">
                  {name && name !== `` ? (
                    <RoutalTooltip title={name}>
                      <Text
                        variant="button"
                        onClick={(e: any) => goToDetails({ target: `organization`, id: organization.id })}
                      >
                        {name.length > 35 ? `${name.substring(0, 35)}...` : `${name}`}
                      </Text>
                    </RoutalTooltip>
                  ) : (
                    <Text
                      variant="button"
                      onClick={(e: any) => goToDetails({ target: `organization`, id: organization.id })}
                      style={{
                        fontWeight: 500,
                        color: RoutalPalette.secondary00,
                      }}
                      intlId="no_name"
                    />
                  )}
                </Grid>
              );
            },
          });
          break;
        case `default_project`:
          columns.push({
            width: 200,
            height: 45,
            sortable,
            accessorName: `default_project`,
            name: (
              <Text
                style={{ color: RoutalPalette.primary20 }}
                isBold
                intlId="backoffice.clients.users.default_project"
              />
            ),
            formatter: ({ doc: user }: { doc: BackofficeUser }) => {
              const defaultProject = user.projects.find((p) => p.id === user.organization.default_project_id);

              if (!defaultProject) {
                return null;
              }

              const { label } = defaultProject;

              return (
                <Grid container direction="row" alignItems="center">
                  {label && label !== `` ? (
                    <RoutalTooltip title={label}>
                      <Text
                        variant="button"
                        onClick={(e: any) => goToDetails({ target: `project`, id: defaultProject.id })}
                      >
                        {label.length > 35 ? `${label.substring(0, 35)}...` : `${label}`}
                      </Text>
                    </RoutalTooltip>
                  ) : (
                    <Text
                      variant="button"
                      onClick={(e: any) => goToDetails({ target: `project`, id: defaultProject.id })}
                      style={{
                        fontWeight: 500,
                        color: RoutalPalette.secondary00,
                      }}
                      intlId="no_name"
                    />
                  )}
                </Grid>
              );
            },
          });
          break;
        case `phone`:
          columns.push({
            width: 120,
            columnStyle: {
              width: `unset`,
            },
            accessorName: `phone`,
            align: `center` as AlignmentTypes,
            sortable,
            name: <Text style={{ color: RoutalPalette.primary20 }} isBold intlId="backoffice.clients.users.phone" />,
            formatter: ({ value }) =>
              value && (
                <Text variant="body" style={{ maxWidth: `220px`, userSelect: `none` }}>
                  {value}
                </Text>
              ),
          });
          break;
        case `validation`:
          columns.push({
            width: 120,
            columnStyle: {
              width: `unset`,
            },
            accessorName: `validation`,
            align: `center` as AlignmentTypes,
            sortable,
            name: (
              <Text style={{ color: RoutalPalette.primary20 }} isBold intlId="backoffice.clients.users.validation" />
            ),
            formatter: ({ value: validation }: { value: UserBaseValidation }) =>
              validation.email_validated ? (
                <Check style={{ color: `#70D292` }} />
              ) : (
                <Close style={{ color: `#E03030` }} />
              ),
          });
          break;
        case `language`:
          columns.push({
            width: 120,
            columnStyle: {
              width: `unset`,
            },
            accessorName: `language`,
            align: `center` as AlignmentTypes,
            sortable,
            name: <Text style={{ color: RoutalPalette.primary20 }} isBold intlId="backoffice.clients.users.language" />,
            formatter: ({ value: language }: { value: Language }) =>
              language && (
                <Text
                  variant="body"
                  style={{ maxWidth: `220px`, userSelect: `none` }}
                  intlId={`settings.account.profile.${language.value}`}
                />
              ),
          });
          break;
        default:
          break;
      }
    }
  });

  return [
    ...columns,
    ...(tableColumns.find(({ displayable, identifier }) => identifier === `created_at` && displayable)
      ? [
          {
            width: 60,
            columnStyle: {
              width: `unset`,
            },
            accessorName: `created_at`,
            align: `center` as AlignmentTypes,
            sortable: true,
            name: (
              <Text style={{ color: RoutalPalette.primary20 }} isBold intlId="backoffice.clients.users.created_at" />
            ),
            formatter: ({ value: createdAt }) => (
              <RoutalTooltip title={dateFormat(createdAt)}>
                <Text variant="body">{pastDateTime(createdAt)}</Text>
              </RoutalTooltip>
            ),
          } as DataTableColumnProps,
        ]
      : []),
    {
      column: `actions`,
      name: <Text style={{ color: RoutalPalette.primary20 }} isBold intlId="backoffice.clients.users.actions" />,
      align: `center`,
      width: 140,
      columnStyle: {
        width: `120px`,
      },
      valueCellStyle: {
        width: `100%`,
      },
      formatter: ({ doc: user }: { doc: BackofficeUser }) => (
        <Grid container direction="row" wrap="nowrap" justifyContent="center" alignItems="center">
          {user.id !== adminUser?.id && user.id !== loggedUser?.id ? (
            <>
              <Button
                styleType="secondary"
                onClick={() => handleAsUser(user.id)}
                style={{
                  padding: `6px 10px`,
                }}
              >
                <TransferWithinAStation />
              </Button>

              <HSpacer medium />
            </>
          ) : null}

          <BackofficeClientsUserActionsMenu
            user={user}
            type="popover"
            popoverProps={{
              hideArrow: true,
              content: <MoreHoriz />,
              containerStyle: {
                width: `unset`,
              },
              style: { height: `18px`, color: RoutalPalette.secondary60, padding: 0, margin: 0, border: `unset` },
            }}
            refreshAfterUpdate={() => onRefreshContent()}
          />
        </Grid>
      ),
    },
  ] as Array<DataTableColumnProps>;
};
